<template>
    <div>
        <swiper :options="swiperOption" ref="swiper" v-on="$listeners">
            <slot name="default"><!-- --></slot>
            <div :id="`${uniqId}_next`" v-if="showArray" class="u-swiper-button u-swiper-button__next"
                 :style="{'background': backgroundColor}" slot="button-next"></div>
            <div :id="`${uniqId}_prev`" v-if="showArray" class="u-swiper-button u-swiper-button__prev"
                 :style="{'background': backgroundColor}" slot="button-prev"></div>
            <!--  <div v-if="hasCounter && isVisible" slot="pagination" class="c-swiper-counter">
                    {{ slideIndex + 1 }}&nbsp;/&nbsp;{{ slidesCount }}
                  </div>-->
            <div :id="`${uniqId}_pagination`" v-if="hasCounter" slot="pagination" class="pagination c-swiper-bullet">
            </div>
        </swiper>
        <slot name="gallery" :items="items"><!-- Slot for a PhotoSwipe gallery --></slot>
    </div>
</template>
<script lang="ts">
import forEach from 'lodash/forEach';
import throttle from 'lodash/throttle';
import { Component, Prop } from 'vue-property-decorator';
import { dataLayer } from '../globals';
import { EventBus } from '../helpers/bus';
import BaseComponent from './Base';
import Swiper from './Swiper.vue';
import { ISwiperComponent, Slide } from './SwiperTypes';

@Component({
  components: {
    Swiper,
  },
})
export default class HorizontalSwiper extends BaseComponent {
  public readonly $refs: {
    swiper: any,
  };

  @Prop({ type: Number, default: 768 }) protected readonly minWidth: number;
  @Prop({ type: [String, Number, Function], default: 'auto' }) protected readonly slidesPerView: any;
  @Prop({ type: Boolean, default: true }) protected readonly preventClick: boolean;
  @Prop({ type: Boolean, default: false }) protected readonly isAdaptive: boolean;
  @Prop({ type: Boolean, default: false }) protected readonly hasCounter: boolean;
  @Prop({ type: Number, default: 0 }) protected readonly slideWidth: number;
  @Prop({ type: String, default: 'Horizontal Swiper' }) protected readonly gtmCategory: string;
  @Prop({ type: String, default: 'transparent' }) protected readonly backgroundColor: string;
  @Prop({ type: String, default: 'block' }) protected readonly showButton: string;


  protected items: any[] = [];
  protected isVisible: boolean = false;
  protected pausedFor5Seconds: boolean = false;
  protected slidesCount: number = 1;
  protected slideIndex: number = 0;
  protected showArray: boolean = true;
  protected swiperOption: any = {};

  protected get swiper(): ISwiperComponent {
    return (this.$refs.swiper as ISwiperComponent);
  }

  protected updateSlidesPerView(slides: number | string): void {
    this.swiperOption.slidesPerView = slides;
    this.swiper.setSlidesPerView(slides);
  }

  protected updateIfVisible(): any {
    if (!this.isVisible && this.$el.offsetParent !== null) {
      this.isVisible = true;
      this.init();
    }
  }

  protected init(): any {
    if (!this.isVisible) {
      return;
    }
    if (this.showButton === 'none') {
      this.showArray = false;
    }
    forEach(this.swiper.getSlides(), (slide: Slide, index) => {
      const link: HTMLAnchorElement | null = (slide as HTMLElement).querySelector('a');

      // It's ugly fix for ugly solution.
      // webp, jpg, jpeg, gif, png
      if (link === null
          || (!['.webp', '.jpg', '.jpeg', '.png', '.gif'].find((item) => link.href.includes(item)) && !link.classList.contains('c-tile--video'))
      ) {
        return;
      }

      // TODO Image sizes issue
      const src: string = link ? (link.href || '') : '';
      if (!link.classList.contains('c-tile--video')) {
        const item: any = {
          src,
          title: (slide as HTMLElement).dataset.title,
          initialPosition: { x: 0, y: 0 },
        };
        this.items.push(item);
        const img = new Image();
        img.addEventListener('load', function () {
          item.w = this.naturalWidth;
          item.h = this.naturalHeight;
        });
        img.src = src;
      } else {
        this.items.push({
          src: '',
          html: '<div class="u-video-center"><div class="pswp__video-container"><div is="youtube-video" class="pswp__video" src="' + link.href + '"></div></div></div>',
        });
      }
      link.addEventListener('click', (event) => {
        dataLayer.push({
          eventAction: 'Slide click',
          eventCategory: this.gtmCategory,
          event: 'horizontalSwiper:slideClick',
        });
        this.$emit('slideClick', this.items, index, slide, this.swiper, event);
        if (this.preventClick) {
          event.preventDefault();
        }
      });
    });

    if (this.slideWidth > 0 && this.slidesPerView !== 'auto') {
      this.onResize({ width: this.$el.offsetWidth });
      EventBus.$on('windowResize', () => {
        this.onResize({ width: this.$el.offsetWidth });
      });
    } else if (window.innerWidth < this.minWidth) {
      this.updateSlidesPerView(1);
    }

    this.$emit('init', this.$refs.swiper);
    this.swiper.update();
  }

  /**
   * This function must be triggered only if there is a number of slides set.
   *
   * @param {number} width  Parent element width
   */
  protected onResize({ width }: { width: number }): void {
    const slidesCount = this.swiperOption.slidesPerView;
    const maxSlidesPerView = parseInt(this.slidesPerView, 10);
    let slidesCanFit = Math.round(width / this.slideWidth);
    if (slidesCanFit > maxSlidesPerView) {
      slidesCanFit = maxSlidesPerView;
    }
    if (slidesCanFit < 1) {
      slidesCanFit = 1;
    }
    if (slidesCount !== slidesCanFit) {
      this.updateSlidesPerView(slidesCanFit);
    }
  }

  protected updateIndexes(): void {
    setTimeout(() => {
      this.slideIndex = this.swiper.getSlideIndex();
      this.slidesCount = this.swiper.getSlides().length;
    }, 50);
  }

  protected beforeMount(): void {
    this.swiperOption = {
      slidesPerView: this.slidesPerView,
      initialSlide: 0,
      watchOverflow: true,
      simulateTouch: false,
      autoHeight: this.isAdaptive,
      breakpoints: {
        390: {
          slidesPerView: 1,
          spaceBetween: 15,
        },
      },
      navigation: {
        nextEl: `#${this.uniqId}_next`,
        prevEl: `#${this.uniqId}_prev`,
      },
      pagination: {
        el: `#${this.uniqId}_pagination`,
        clickable: true,
        /*renderBullet: function(index, className) {
         return `<span class="dot swiper-pagination-bullet">${index}</span>`;
         },*/
      },
    };
  }

  protected mounted(): void {
    this.updateIfVisible();
    const updateManually = throttle(this.updateIfVisible, 150, { leading: false });
    this.$refs.swiper.$on('slideChange', () => {
      dataLayer.push({
        eventAction: 'Slide change',
        eventCategory: this.gtmCategory,
        event: 'horizontalSwiper:slideChange',
      });
      if (this.hasCounter) {
        this.updateIndexes();
      }
    });

    if (this.hasCounter) {
      this.updateIndexes();
    }
    EventBus.$on('updateSwipers', updateManually);
  }
}
</script>
<style lang="scss">
.u-h2 {
    font-size: 24px;
}

.c-blog-post {
    .c-tile {
        /* border-radius: 20px; */
        margin: 0 7px;

        &__inner {
            padding-left: 50px;
        }
    }

    .swiper-container {
        margin-left: -8px;
        margin-right: -7px;

        .swiper-slide {
            margin-right: 0;
        }

        .u-swiper-button {
            font-size: 52px !important;
            color: transparent;
            position: absolute;
            display: inline-block;
            top: 0;
            margin-top: -24px;
            z-index: 10;
            cursor: pointer;
            font-family: 'FontAwesome';

            &__prev {
                &:before {
                    position: relative !important;
                    border-radius: 26px;
                    top: 35%;
                    margin-top: 0 !important;
                    height: 52px !important;
                    width: 52px !important;
                    background-color: #ff6700;
                    font-size: 30px !important;
                    padding: 10px;
                    color: #f5e6d2;
                    content: "\f177";
                }
            }

            &__next {
                right: 0;

                &:before {
                    position: relative !important;
                    border-radius: 26px;
                    top: 35%;
                    margin-top: 0 !important;
                    height: 52px !important;
                    width: 52px !important;
                    background-color: #ff6700;
                    font-size: 30px !important;
                    padding: 10px;
                    color: #f5e6d2;
                    content: "\f178";
                    right: -10px;
                }
            }

            &.swiper-button-disabled {
                opacity: .5;
            }
        }
    }
}

.c-blog-posts {
    .c-tile {
        border-radius: 20px;

        &__background__image {
            border-top-left-radius: 20px;
            border-top-right-radius: 20px;
        }

        &__content {
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
        }
    }

    .u-swiper-button {
        font-size: 52px !important;
        color: transparent;
        position: absolute;
        display: inline-block;
        top: 0;
        margin-top: -24px;
        z-index: 10;
        cursor: pointer;
        font-family: 'FontAwesome';

        &__prev {
            &:before {
                position: relative !important;
                border-radius: 26px;
                top: 35%;
                margin-top: 0 !important;
                height: 52px !important;
                width: 52px !important;
                background-color: #ff6700;
                font-size: 30px !important;
                padding: 10px;
                color: #f5e6d2;
                content: "\f177";
            }
        }

        &__next {
            &:before {
                position: relative !important;
                border-radius: 26px;
                top: 35%;
                margin-top: 0 !important;
                height: 52px !important;
                width: 52px !important;
                background-color: #ff6700;
                font-size: 30px !important;
                padding: 10px;

                /* padding-right: 60px !important; */
                color: #f5e6d2;
                content: "\f178";
                right: 10px;
            }
        }

        &.swiper-button-disabled {
            opacity: .5;
        }
    }

    &__with-background-image {
        .swiper-container {
            .u-swiper-button {
                width: 40px !important;
            }
        }
    }
}

.u-swiper-button {
    font-size: 24px;
    color: #fff;
    position: absolute;
    display: inline-block;
    top: 50%;
    margin-top: -24px;
    z-index: 10;
    cursor: pointer;
    font-family: 'FontAwesome';

    &__prev {
        left: 10px;

        &:before {
            content: "\f053";
        }
    }

    &__next {
        right: 10px;

        &:before {
            content: "\f054";
        }
    }

    &.swiper-button-disabled {
        opacity: .5;
    }
}

/* set round button */
.main-tour-swiper, .c-tour-destination {
    .hidden-in-big {
        .swiper-slide {
            .c-tile__background__image {
                background-size: cover !important;
            }
        }
    }

    .swiper-container {
        padding-left: 60px;
        padding-right: 60px;
        padding-bottom: 60px;

        .swiper-pagination-bullet-active {
            opacity: 1;
            background: #ff6700 !important;
        }

        .u-swiper-button {
            font-size: 52px !important;
            color: transparent;
            position: absolute;
            display: inline-block;
            top: 0;
            margin-top: -24px;
            z-index: 10;
            cursor: pointer;
            font-family: 'FontAwesome';

            &__prev {
                &:before {
                    position: relative !important;
                    border-radius: 26px;
                    top: 35%;
                    margin-top: 0 !important;
                    height: 52px !important;
                    width: 52px !important;
                    background-color: #ff6700;
                    left: 35px;
                    font-size: 30px !important;
                    padding: 10px;
                    color: #f5e6d2;
                    content: "\f177";
                }
            }

            &__next {
                &:before {
                    position: relative !important;
                    border-radius: 26px;
                    top: 35%;
                    margin-top: 0 !important;
                    height: 52px !important;
                    width: 52px !important;
                    background-color: #ff6700;
                    left: -25px;
                    font-size: 30px !important;
                    padding: 10px;
                    color: #f5e6d2;
                    content: "\f178";
                }
            }

            &.swiper-button-disabled {
                opacity: .5;
            }
        }
    }
}

@media only screen and (min-width: 783px) {
    .main-tour-dayprogram .swiper-container {
        margin-right: 60px;
    }
}

.main-tour-dayprogram, .c-tour-extension, .c-accommodation {
    .swiper-container {
        margin-bottom: 40px;
    }

    .swiper-pagination-bullets {
        display: none;
    }

    .u-swiper-button:before {
        position: absolute !important;
        font-size: 24px;
        line-height: 40px;
        height: 40px;
        width: 60px;
        margin-top: -20px;
    }

    .u-swiper-button__next:before {
        right: 50%;
        margin-right: -20px;
    }

    .u-swiper-button__prev:before {
        left: 50%;
        margin-left: -20px;
    }
}

.c-tour-extension {
    .swiper-container {
        margin-top: 20px;
    }
}

.c-tour-destination {
    .swiper-container {
        padding-left: 60px;
        padding-right: 60px;
        padding-bottom: 60px;

        .u-swiper-button__prev:before, .u-swiper-button__next:before {
            top: 32%;
        }

        @media only screen and (max-width: 782px) {
            .u-swiper-button__prev:before, .u-swiper-button__next:before {
                top: 27% !important;
            }
        }
    }
}

.c-blog-article {
    margin-top: 30px;

    .u-swiper-button {
        &__next {
            right: 0 !important;
        }
    }
}

.swiper-container > .swiper-pagination-bullets {
    z-index: 999 !important;
}
</style>
